import { isSessionError } from '../hooks/useSessionGuard';

const MAX_DETAIL_LENGTH = 500;
const MAX_DETAIL_DEPTH = 6;

const describe = (reason: object) => {
  const ancestors: unknown[] = [];

  try {
    const json = JSON.stringify(
      reason,
      function replacer(this: unknown, _key, value: unknown) {
        if (typeof value !== 'object' || value === null) return value;
        // Unwind to the holder first, or a node reachable twice reads as a cycle.
        while (ancestors.length > 0 && ancestors[ancestors.length - 1] !== this)
          ancestors.pop();
        if (ancestors.includes(value)) return '[Circular]';
        // Only the first 500 chars survive, so walking a deep graph is wasted work.
        if (ancestors.length >= MAX_DETAIL_DEPTH) return '[Depth]';
        ancestors.push(value);
        return value;
      },
    );
    if (json !== undefined && json !== '{}')
      return json.slice(0, MAX_DETAIL_LENGTH);
  } catch {
    /* a throwing getter or a BigInt */
  }

  return Object.prototype.toString.call(reason);
};

/** App Insights reads `message`/`name`/`stack` off an `Error`; anything else lands as "[object Object]". */
export const toError = (reason: unknown, fallbackName: string): Error => {
  if (reason instanceof Error) return reason;

  if (typeof reason === 'object' && reason !== null) {
    const { message, name, stack } = reason as {
      message?: unknown;
      name?: unknown;
      stack?: unknown;
    };
    const error = new Error(
      typeof message === 'string' && message
        ? message
        : Object.prototype.toString.call(reason),
    );
    error.name = typeof name === 'string' && name ? name : fallbackName;
    // The serialized payload can embed customer data, so it rides `stack` (exceptions only).
    error.stack = typeof stack === 'string' && stack ? stack : describe(reason);
    return error;
  }

  const error = new Error(String(reason));
  error.name = fallbackName;
  return error;
};

/** axios' CanceledError, by shape: this module must never load axios (jest cannot). */
export const isAxiosCancel = (reason: unknown): boolean => {
  if (typeof reason !== 'object' || reason === null) return false;
  const {
    __CANCEL__: cancelled,
    code,
    name,
  } = reason as {
    __CANCEL__?: unknown;
    code?: unknown;
    name?: unknown;
  };
  return (
    cancelled === true ||
    code === 'ERR_CANCELED' ||
    name === 'CanceledError' ||
    name === 'AbortError'
  );
};

/** A session-expiry error sends the person to sign in; a cancelled request was asked for. */
export const isControlFlowRejection = (reason: unknown): boolean =>
  isSessionError(reason) || isAxiosCancel(reason);
